/**
 * Operational Issues inbox: identity, lifecycle, and action routing.
 * Presentation helpers only — persistence lives in record_integration_exception
 * / get_unified_exceptions (migration 0165).
 */

export const ACTIVE_ISSUE_STATUSES = ["open", "acknowledged"] as const;
export type ActiveIssueStatus = (typeof ACTIVE_ISSUE_STATUSES)[number];

export const DEAD_LETTER_DEDUP_KEY = "dead_letter:failed_domain_events";

const SEP = "\u001f";

export function integrationIssueFingerprint(opts: {
  orgId: string;
  connectorId?: string | null;
  exceptionType: string;
  entityType?: string | null;
  entityRef?: string | null;
}): string {
  return [
    opts.orgId,
    opts.connectorId ?? "none",
    opts.exceptionType,
    opts.entityType ?? "",
    opts.entityRef ?? "",
  ].join(SEP);
}

export function observationAgainstActiveIssue(existingStatus: string | null | undefined): "create" | "touch" {
  if (!existingStatus) return "create";
  if ((ACTIVE_ISSUE_STATUSES as readonly string[]).includes(existingStatus)) return "touch";
  if (existingStatus === "ignored") return "touch";
  return "create";
}

export function defaultOperationalStatusFilter(): "actionable" {
  return "actionable";
}

export function statusMatchesOperationalFilter(status: string, source: string, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "actionable") {
    if (source === "provider") return true;
    return (ACTIVE_ISSUE_STATUSES as readonly string[]).includes(status);
  }
  return status === filter;
}

export function skuFromUnmappedMessage(message: string): string {
  const incoming = /Incoming SKU:\s*(\S+)/i.exec(message ?? "");
  if (incoming?.[1]) return incoming[1];
  const trimmed = (message ?? "").trim();
  if (!trimmed) return "";
  return trimmed.split(" · ")[0]?.replace(/^SKU\s+/i, "") ?? trimmed;
}

export function isObsoleteE2EUnmappedSku(entityRef: string | null | undefined): boolean {
  return (entityRef ?? "").startsWith("BORRAQ-E2E-ORDER-SKU-");
}

export function shouldAutoResolveRefundSyncFailure(opts: {
  providerSyncStatus?: string | null;
  providerRefundId?: string | null;
}): boolean {
  return opts.providerSyncStatus === "confirmed" && Boolean((opts.providerRefundId ?? "").trim());
}

export function shouldAutoResolveUnmappedProduct(mappedThroughAuthority: boolean): boolean {
  return mappedThroughAuthority;
}

export function exceptionInboxStatus(filter: "open" | "resolved" | "all", status: string): boolean {
  if (filter === "all") return true;
  if (filter === "open") return status === "open" || status === "acknowledged";
  return status === "resolved" || status === "ignored";
}

export function exceptionTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    unmapped_product: "Unmapped product",
    order_import_failed: "Order import failed",
    mapping_conflict: "Mapping conflict",
    inventory_publish_failed: "Inventory publish failed",
    reconciliation: "Reconciliation",
    webhook_dedup_conflict: "Webhook conflict",
    refund_provider_sync_failed: "Provider refund sync failed",
    refund_provider_rejected: "Provider refund rejected",
    buyer_cancellation_requested: "Buyer requested cancellation",
    buyer_return_opened: "Buyer opened a return",
    buyer_item_not_received: "Buyer reports item not received",
    buyer_item_not_as_described: "Buyer reports item not as described",
    ebay_case_requires_attention: "eBay case requires attention",
    payment_dispute_requires_attention: "Payment dispute requires attention",
    inbound_family_poll_blocked: "Marketplace after-sales poll unavailable",
  };
  return labels[type] ?? type.replaceAll("_", " ");
}

export function exceptionPrimaryRef(opts: {
  exceptionType: string;
  entityRef?: string | null;
  providerRefundId?: string | null;
  providerLabel?: string | null;
}): string {
  if (
    (opts.exceptionType === "refund_provider_sync_failed" || opts.exceptionType === "refund_provider_rejected") &&
    opts.providerRefundId
  ) {
    return `${opts.providerLabel || "Provider"} refund ${opts.providerRefundId}`;
  }
  const ref = (opts.entityRef ?? "").trim();
  if (!ref) return "";
  if (opts.exceptionType === "unmapped_product") return `SKU ${ref}`;
  return ref;
}

export function operationalIssueAction(opts: {
  source: string;
  type: string;
  actionHint: string;
  message: string;
  entityRef?: string | null;
}): { label: string; path: string | null } {
  if (opts.type === "unmapped_product" || opts.actionHint === "map_incoming_sku") {
    const sku = (opts.entityRef ?? "").trim() || skuFromUnmappedMessage(opts.message);
    const q = sku ? `?sku=${encodeURIComponent(sku)}` : "";
    return { label: "Map incoming SKU", path: `/integrations/exceptions${q}` };
  }
  const catalog: Record<string, { label: string; path: string | null }> = {
    review_credentials: { label: "Review Credentials", path: "/integrations" },
    retry_later: { label: "Review Connector", path: "/integrations" },
    review_listing: { label: "Review Listing", path: "/listings" },
    retry_publish: { label: "Retry Publish", path: "/listings" },
    review_shipment: { label: "Review Shipment", path: "/fulfilment?tab=shipments" },
    review_rate: { label: "Review Rate", path: "/fulfilment?tab=shipments" },
    retry_label: { label: "Retry Label", path: "/fulfilment?tab=shipments" },
    review_inventory: { label: "Review Inventory", path: "/inventory" },
    review_order: { label: "Review Order", path: "/sales-orders" },
    review_sales_order: { label: "Open sales order", path: opts.entityRef && /^[0-9a-f-]{36}$/i.test(opts.entityRef) ? `/sales-orders/${opts.entityRef}` : "/sales-orders" },
    review_receipt: { label: "Review Receipt", path: "/purchase-orders" },
    review_variants: { label: "Review Variants", path: "/products" },
    review_connector: { label: "Review Connector", path: "/integrations" },
    review_stocktake: { label: "Review Stocktake", path: "/stocktake" },
    retry_sync: { label: "View Sync", path: "/integrations/automation" },
    retry_event: { label: "View Events", path: "/integrations/automation" },
    review_automation: { label: "Review Automation", path: "/automation" },
    map_incoming_sku: { label: "Map incoming SKU", path: "/integrations/exceptions" },
    review: { label: "Review", path: null },
  };
  return catalog[opts.actionHint] ?? catalog.review;
}

export function mergeExternalOrderIds(existing: unknown, incoming: unknown): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (v: unknown) => {
    if (v == null) return;
    const s = String(v).trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  if (Array.isArray(existing)) existing.forEach(push);
  else push(existing);
  if (Array.isArray(incoming)) incoming.forEach(push);
  else push(incoming);
  return out;
}

export function anomalyScanCreatesDuplicate(firstKey: string, secondKey: string): boolean {
  return firstKey === secondKey;
}
